import {
  useAnimationFrame,
  useMotionValue,
  useTransform,
  wrap,
} from 'motion/react';

export type UseMarqueeAnimationParams = {
  /**
   * Measured in pixels/second
   * @default 5
   */
  speed?: number;
  /**
   * Direction of the marquee animation.
   * Follows cartesian coordinates. On the x-axis,
   *   +1 moves L -> R
   *   -1 moves R -> L
   * @default -1
   */
  direction?: 1 | -1;
  /**
   * Amount of pixels to move before detecting a drag event
   * @default 5
   */
  dragThreshold?: number;
};

export const useMarqueeAnimation = ({
  speed = 2.5,
  direction = -1,
  dragThreshold = 5,
}: UseMarqueeAnimationParams) => {
  const baseX = useMotionValue(0);
  const velocity = speed * direction;

  const baseDragX = useMotionValue(0);

  type DragState = 'idle' | 'pending' | 'dragging';
  type PlayState = 'idle' | 'paused' | 'running';
  const dragState = useMotionValue<DragState>('idle');
  const playState = useMotionValue<PlayState>('idle');

  useAnimationFrame((time, delta) => {
    if (playState.get() === 'idle') {
      playState.set('running');
    }

    let deltaX = 0;
    if (playState.get() === 'running') {
      deltaX = velocity * (delta / 1000);
    }
    if (playState.get() !== 'running' && dragState.get() === 'dragging') {
      const x1 = baseDragX.getPrevious() ?? 0;
      const x2 = baseDragX.get();
      deltaX = -velocity * ((x2 - x1) / 100);
    }
    baseX.set(baseX.get() + deltaX);
  });

  const x = useTransform(baseX, (v) => {
    const wrapped = wrap(0, -100, v);
    return `${wrapped}%`;
  });

  const pauseMarquee = () => {
    playState.set('paused');
  };

  const playMarquee = () => {
    playState.set('running');
  };

  const prepareDrag = (x: number) => {
    playState.set('paused');
    dragState.set('pending');
    baseDragX.set(x);
  };

  const startDrag = () => {
    playState.set('paused');
    dragState.set('dragging');
  };

  const updateDrag = (x: number) => {
    if (
      dragState.get() === 'pending' &&
      Math.abs(x - baseDragX.get()) > dragThreshold
    ) {
      startDrag();
    }

    if (dragState.get() === 'dragging') {
      baseDragX.set(x);
    }
  };

  const endDrag = (x: number) => {
    baseDragX.set(x);
    dragState.set('idle');
  };

  return { x, playMarquee, pauseMarquee, prepareDrag, endDrag, updateDrag };
};
